import {ICartPromotion} from "@connox/typescript-library/types/cart/CartPromotion";
import window from "../components/lib/WindowExtended";
import {GtinItem} from "@connox/typescript-library";

class UrlSearchParamsHelper {
  private cartItems: GtinItem[] = [];
  private wishlistItem: number | null = null;
  private cartPromotion?: ICartPromotion;
  private cartVoucherCode: string | null = null;
  private readonly searchQuery: string;
  private readonly urlSearchParams: URLSearchParams;

  constructor() {
    this.searchQuery = window.location.search;
    this.urlSearchParams = new URLSearchParams(this.searchQuery);

    this.parseURLSearchParams();
  }

  /**
   * Returns the items parsed from the URLSearchParams
   * (includes the additionalItemIds with quantity 1)
   */
  get itemIds() {
    return this.cartItems;
  }

  /* Returns the promotion parsed from the URLSearchParams */
  get promotions() {
    return this.cartPromotion;
  }

  /* Returns the voucherCode parsed from the URLSearchParams */
  get voucherCode() {
    return this.cartVoucherCode;
  }

  /* Returns a wishlist item to add */
  get wishlistItemId() {
    return this.wishlistItem;
  }

  /* Convenience Getter */
  public containsParams() {
    return this.cartItems.length || this.cartPromotion !== undefined || this.cartVoucherCode !== null;
  }

  /* Returns whether the URLSearchParams contain additional items */
  private hasAdditionalItems() {
    return this.searchQuery.includes("AdditionalItemIds");
  }

  /* Returns whether the URLSearchParams contain items */
  private hasItem(): boolean {
    return (
      (this.urlSearchParams.get("Action") === "AddItem" && this.searchQuery.includes("ItemId")) ||
      this.hasAdditionalItems()
    );
  }

  /* Returns whether the URLSearchParams contain wishlist items */
  private hasWishlistItem(): boolean {
    return this.urlSearchParams.get("Action") === "AddItemToWishlist" && this.searchQuery.includes("ItemId");
  }

  /* Returns whether the URLSearchParams contain a promotion */
  private hasPromotions(): boolean {
    return promotionIdentifiers.some((p) => this.urlSearchParams.has(p));
  }

  /* Returns whether the URLSearchParams contain a voucherCode */
  private hasVoucher(): boolean {
    return this.searchQuery.includes("AddVoucher") && this.searchQuery.includes("Code");
  }

  /* Method to parse the URLSearchParams */
  private parseURLSearchParams() {
    if (this.hasPromotions()) {
      promotionIdentifiers.some((p) => {
        const promotionId = this.urlSearchParams.get(p);

        if (promotionId) {
          const pcrCandidate = promotionCandidates.split(",").find((candidate) => this.urlSearchParams.has(candidate));
          const pcr = pcrCandidate && (this.urlSearchParams.get(pcrCandidate) ?? undefined);

          this.cartPromotion = {id: Number(promotionId), reference: pcr};

          return true;
        }

        return false;
      });
    }

    if (this.hasVoucher()) {
      this.cartVoucherCode = this.urlSearchParams.get("Code");
      this.urlSearchParams.delete("Action");
      this.urlSearchParams.delete("Code");
    }

    if (this.hasItem()) {
      this.cartItems.push({
        itemId: Number(this.urlSearchParams.get("ItemId")),
        quantity: this.urlSearchParams.get("Quantity") !== null ? Number(this.urlSearchParams.get("Quantity")) : 1,
        gtin: null,
      });

      if (this.hasAdditionalItems()) {
        const additionalItemIds = this.urlSearchParams.get("AdditionalItemIds");

        if (additionalItemIds) {
          additionalItemIds.split(",").map((splittedItemId) => {
            this.cartItems.push({
              itemId: Number(splittedItemId),
              quantity: 1,
              gtin: null,
            });
          });
        }
      }

      this.urlSearchParams.delete("Action");
      this.urlSearchParams.delete("ItemId");
      this.urlSearchParams.delete("Quantity");
      this.urlSearchParams.delete("AdditionalItemIds");
    }

    if (this.hasWishlistItem()) {
      this.wishlistItem = Number(this.urlSearchParams.get("ItemId"));
      this.urlSearchParams.delete("Action");
      this.urlSearchParams.delete("ItemId");
    }

    const {origin, pathname} = window.location;
    const currentPage = origin + pathname;
    const searchParamsString = this.urlSearchParams.toString();
    const cleanedUpUrlParams = searchParamsString && "?" + searchParamsString;
    history.replaceState(history.state, "", currentPage + cleanedUpUrlParams + window.location.hash);
  }
}

const promotionIdentifiers = ["pid", "PID", "p", "P"];
const promotionCandidates = "ref,zanpid,pcr,belboon,idealoid";

const paramsHelper = new UrlSearchParamsHelper();

export {paramsHelper as UrlSearchParamsHelper};
